import type { Client } from '../client';

export interface IntakePatientData {
  patient_name?: string;
  name?: string;
  identity_type?: 'national_id' | 'iqama' | 'passport' | 'other' | null;
  national_id?: string | null;
  phone?: string | null;
  nationality?: string | null;
  referral_cause?: string | null;
  source_provider?: string | null;
  ref_type?: string | null;
  required_specialty?: string | null;
  /** GlobeMed idSpecialty when the value was picked from the catalogue. */
  required_specialty_code?: string | null;
  bed_type?: string | null;
  payment_method?: 'cash' | 'insurance' | null;
}

export type IntakeTokenStatus = 'active' | 'used' | 'revoked' | 'expired';

export interface IntakeToken {
  id: number;
  /** Null unless the invitation is still active — a dead link is not re-sendable. */
  token: string | null;
  hospital_id: string;
  patient_hint: string | null;
  note: string | null;
  created_by: string | null;
  created_at: string;
  expires_at: string;
  used_at: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  approval_id: number | null;
  status: IntakeTokenStatus;
}

export type IntakeDocType =
  | 'identity_document'
  | 'medical_report'
  | 'no_insurance_proof'
  | 'other';

/** Work-queue views: triage → review → ready (accepted, awaiting the manual
 * GlobeMed hand-off) → submitted; declined is the reject archive. */
export type IntakeCaseView = 'triage' | 'review' | 'ready' | 'submitted' | 'declined';

export interface IntakeNote {
  id: number;
  author: string;
  note: string;
  followup_at: string | null;
  created_at: string;
}

export interface IntakeCase {
  id: number;
  hospital_id: string;
  ihalaty_reference: string;
  patient_info: Record<string, unknown> | string;
  origin: string;
  intake_source: string | null;
  triage_status: string;
  priority: string | null;
  submitted_specialty: string | null;
  department_id: number | null;
  department_name: string | null;
  department_code: string | null;
  status: string;
  decision: string | null;
  decided_at: string | null;
  decided_by_name: string | null;
  decision_reason: string | null;
  created_at: string;
  followup_at: string | null;
  globemed_submitted_at: string | null;
  globemed_submitted_by: string | null;
  globemed_reference: string | null;
  notes: IntakeNote[];
  attachments: Array<{
    id: number;
    filename: string;
    file_size: number;
    mime_type: string | null;
    attachment_type: string;
  }>;
}

export interface MintTokenResult {
  token: string;
  expires_at: string;
  submit_path: string;
}

export interface IntakeSettings {
  hospital_id: string;
  hospital_name: string;
  group_id: number | null;
  lifecycle: 'active' | 'disabled' | 'archived';
  intake_enabled: boolean;
  public_intake_enabled: boolean;
}

export interface CreateStaffCase extends IntakePatientData {
  hospital_id: string;
  patient_name: string;
  referral_cause: string;
  department_id?: number | null;
  priority?: 'routine' | 'urgent' | 'emergency';
  consent_obtained: boolean;
}

async function upload(client: Client, path: string, file: File, docType?: IntakeDocType) {
  const form = new FormData();
  form.append('file', file);
  if (docType) form.append('attachment_type', docType);
  const response = await client.raw(path, { method: 'POST', body: form });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail ?? 'Document upload failed');
  }
  return response.json() as Promise<{ id: number; filename: string }>;
}

export function intakeEndpoints(client: Client) {
  return {
    mintToken: (data: { hospital_id: string; patient_hint?: string | null; note?: string | null; ttl_hours?: number; initial_data?: IntakePatientData }) =>
      client.post<MintTokenResult>('/api/intake/tokens', data),

    getSettings: (hospitalId: string) =>
      client.get<IntakeSettings>(`/api/intake/settings?hospital_id=${encodeURIComponent(hospitalId)}`),

    setPublicEnabled: (hospitalId: string, enabled: boolean) =>
      client.put<IntakeSettings>('/api/intake/settings', {
        hospital_id: hospitalId,
        public_intake_enabled: enabled,
      }),

    createStaffCase: (data: CreateStaffCase) =>
      client.post<{ id: number; reference: string; triage_status: string }>('/api/intake/cases', data),

    listTokens: (hospitalId: string) =>
      client.get<IntakeToken[]>(`/api/intake/tokens?hospital_id=${encodeURIComponent(hospitalId)}`),

    revokeToken: (hospitalId: string, tokenId: number) =>
      client.del<{ status: string; id: number }>(
        `/api/intake/tokens/${tokenId}?hospital_id=${encodeURIComponent(hospitalId)}`,
      ),

    listCases: (
      hospitalId: string,
      triageStatus?: string,
      view?: IntakeCaseView,
      page?: { limit?: number; offset?: number },
    ) => {
      const qs = new URLSearchParams({ hospital_id: hospitalId });
      if (triageStatus) qs.set('triage_status', triageStatus);
      if (view) qs.set('view', view);
      if (page?.limit != null) qs.set('limit', String(page.limit));
      if (page?.offset != null) qs.set('offset', String(page.offset));
      return client.get<IntakeCase[]>(`/api/intake/cases?${qs.toString()}`);
    },

    countCases: (hospitalId: string) =>
      client.get<Record<IntakeCaseView, number>>(
        `/api/intake/cases/counts?hospital_id=${encodeURIComponent(hospitalId)}`,
      ),

    triage: (approvalId: number, data: { department_id: number; priority?: string; specialty?: string | null }) =>
      client.post<IntakeCase>(`/api/intake/cases/${approvalId}/triage`, data),

    addNote: (approvalId: number, data: { note: string; followup_at?: string | null }) =>
      client.post<IntakeNote>(`/api/intake/cases/${approvalId}/notes`, data),

    setFollowup: (approvalId: number, followupAt: string | null) =>
      client.put<{ id: number; followup_at: string | null }>(
        `/api/intake/cases/${approvalId}/followup`,
        { followup_at: followupAt },
      ),

    regenerateAcceptanceLetter: (approvalId: number) =>
      client.post<{ status: string }>(
        `/api/intake/cases/${approvalId}/acceptance-letter`,
        {},
      ),

    markGlobemedSubmitted: (approvalId: number, globemedReference?: string) =>
      client.post<{ id: number; globemed_submitted_at: string }>(
        `/api/intake/cases/${approvalId}/globemed-submitted`,
        { globemed_reference: globemedReference ?? null },
      ),

    uploadStaffDocument: (approvalId: number, file: File, docType?: IntakeDocType) =>
      upload(client, `/api/intake/cases/${approvalId}/attachments`, file, docType),

    uploadPatientDocument: (uploadToken: string, file: File, docType?: IntakeDocType) =>
      upload(client, `/api/intake/uploads/${encodeURIComponent(uploadToken)}`, file, docType),
  };
}
